import { Typography } from "@suraasa/placebo-ui"
import Clock from "assets/icons/clock-outline.svg"
import Wallet from "assets/icons/form-wallet.svg"
import GraphIcon from "assets/icons/graph.svg"
import React from "react"

const _featureList = [
  "Planning the Lesson",
  "Communication",
  "Dealing with Challenges",
  "Technology",
  "Preparation",
  "Interpersonal Skills",
  "Qualifications",
  "Interpersonal Skills",
  "Voice & Accent (VNA)",
  "English Proficiency",
  "Managing Cultural Differences",
  "Managing Diversity in the Classroom",
]

interface GraphCardProps {
  iconSrc: string
  heading: string
  children: React.ReactNode
}
const GraphCard: React.FC<GraphCardProps> = ({
  iconSrc,
  heading,
  children,
}) => (
  <div className="flex w-full grow flex-col rounded-xl border-[1.5px] border-onSurface-200">
    <div className="flex h-10 w-full items-center gap-1 rounded-t-xl bg-onSurface-800 px-5 py-[22.5px]">
      <img src={iconSrc} alt="" />
      <Typography variant="title4" className="text-white">
        {heading}
      </Typography>
    </div>
    <div className="grow px-3"></div>
    <div className=" p-3">{children}</div>
  </div>
)

const GraphCardDetails: React.FC<{ details: string }> = ({ details }) => (
  <div className="my-1 mt-2">
    <Typography variant="smallBody" className="text-onSurface-600">
      {details}
    </Typography>
  </div>
)

const FeatureTag: React.FC<{ text: string }> = ({ text }) => (
  <div className="m-0.25 inline-block rounded-3xl border border-success-500 bg-success-50 p-1">
    <Typography variant="strong" className="!text-[10px] text-onSurface-600">
      {text}
    </Typography>
  </div>
)
const CarrierTrajectorySection = () => {
  const graph1 = {
    description: (
      <GraphCardDetails details="Having an international teaching qualification helps you reach your goal significantly faster by solving for skills & regulatory readiness at once. Without the qualification, you take more time as you need to compensate with more experience and courses." />
    ),
    content: (
      <GraphCard iconSrc={Clock} heading="Time Required">
        <div>
          <Typography variant="title2" className="font-bold text-success-500">
            Less is Better
          </Typography>
          <div className="h-4"></div>
          <div className="flex flex-row">
            <div className="h-10 w-0.25 bg-onSurface-200"></div>
            <div className="relative w-full">
              <div className="mt-2 h-5 w-[90%] rounded-r-md bg-onSurface-300"></div>
              <div className="absolute inset-0 mt-2 h-5 w-[30%] rounded-r-md bg-success-500"></div>
            </div>
          </div>
          <div className="h-0.25 w-full bg-onSurface-200"></div>
          <div className="h-1"></div>
          <Typography
            variant="smallBody"
            className="text-center !text-[10px] text-onSurface-600"
          >
            Time Taken
          </Typography>
          <div className="h-2"></div>
          <div className="flex flex-wrap gap-1">
            <div className="flex flex-row gap-0.5">
              <div className="size-2 rounded-sm bg-success-500"></div>
              <Typography
                variant="strongSmallBody"
                className="text-center !text-[10px] text-onSurface-600"
              >
                With International Teaching Qualification
              </Typography>
            </div>

            <div className="flex flex-row gap-0.5">
              <div className="size-2 rounded-sm bg-onSurface-300"></div>
              <Typography
                variant="strongSmallBody"
                className="text-center !text-[10px] text-onSurface-600"
              >
                Without International Teaching Qualification
              </Typography>
            </div>
          </div>
        </div>
      </GraphCard>
    ),
  }

  const graph2 = {
    description: (
      <GraphCardDetails details="Having an international teaching qualification improves the likeliness of seeing success while looking for jobs since schools find your profile more credible. Without the qualification, you are at a disadvantage against the candidates who do have an international qualification." />
    ),
    content: (
      <GraphCard
        iconSrc={Wallet}
        heading="Cumulative Earnings in Next 10 Years"
      >
        <div>
          <div className="flex flex-row items-end justify-center">
            <div className="flex flex-col items-center gap-1">
              <Typography variant="strongSmallBody" className="text-center">
                Without International Teaching Qualification
              </Typography>
              <div className="h-8 w-5 rounded-t-md bg-onSurface-300"></div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Typography variant="strongSmallBody" className="text-center">
                With International Teaching Qualification
              </Typography>
              <div className="h-[180px] w-5 rounded-t-md bg-success-500"></div>
            </div>
          </div>
          <div className="h-0.25 w-full bg-onSurface-200"></div>
          <div className="h-1"></div>
          <Typography
            variant="smallBody"
            className="text-center !text-[10px] text-onSurface-600"
          >
            More is better
          </Typography>
        </div>
      </GraphCard>
    ),
  }

  const graph3 = {
    description: (
      <GraphCardDetails details="The process of acquiring an international teaching qualification also equips you with essential skills required by international schools. Without the qualification, you may end up with a insufficient skillset than the one required to teach successfully in international schools." />
    ),
    content: (
      <GraphCard
        iconSrc={GraphIcon}
        heading="Depth of Pedagogy Skills Developed"
      >
        <div className="flex flex-wrap items-center">
          {_featureList.map((feature, index) => (
            <FeatureTag text={feature} key={index} />
          ))}
          <Typography
            variant="strongSmallBody"
            className="ml-0.25 text-center !text-[10px]"
          >
            and more
          </Typography>
        </div>
      </GraphCard>
    ),
  }

  return (
    <section>
      <div className="h-4"></div>
      <Typography variant="title1" className="text-onSurface-800">
        Your Career Trajectory Analysis
      </Typography>
      <div className="h-5"></div>

      <div className="hidden auto-rows-auto grid-cols-3 gap-x-4 gap-y-0 sm:grid">
        {graph1.content}
        {graph2.content}
        {graph3.content}
        {graph1.description}
        {graph2.description}
        {graph3.description}
      </div>

      <div className="grid auto-rows-auto grid-cols-1 gap-4 sm:hidden">
        <div className="flex flex-col gap-0">
          {graph1.content}
          {graph1.description}
        </div>
        <div className="flex flex-col gap-0">
          {graph2.content}
          {graph2.description}
        </div>
        <div className="flex flex-col gap-0">
          {graph3.content}
          {graph3.description}
        </div>
      </div>
      <div className="h-5"></div>
    </section>
  )
}

export default CarrierTrajectorySection
